import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

import { Button } from '@vds/buttons';
import { Notification } from '@vds/notifications';
import * as actionTypes from './@Redux/actionTypes';
import { selectStateCache, selectIsLoaded, errorsFromForm } from './@selectors';
import { useCheckoutHooks } from './@Redux/checkout.hooks';
import { getExpressCheckoutRequest } from './modules/services/APIRequestBody';

import { FWAContainer } from '../../common/FWAContainer';
import { LANDING, Margin } from '../../common/styles';
import Plans from './Plans';
import { checkoutLabels as expressCheckoutLabels } from './Components/labels';
import Accessories from './Accessories';
import CheckoutInfo from './CheckoutInfo';
// eslint-disable-next-line import/no-named-as-default-member
import DesktopCart from '../OrderSummary/DesktopCart';
// eslint-disable-next-line import/no-named-as-default-member
import MobileCart from '../OrderSummary/MobileCart';
import { goToProcessStep } from '../../../redirection';
import { getSession, isMobile, isDesktopResolution, isMVA } from '../../common/Helpers';
import { Title } from '../../common/Typography/index';
import { usePrevious } from '../../common/hooks';
import { checkCartidAndLQResponse } from '../../common/Helpers/commonLog';
import { eventDispatcher } from '../../common/Tagging';
import { setNavigationTitle } from '../../common/MVAApiCall';

// import StickyCart from '../OrderSummary/StickyCart'

const ExpressCheckout = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isLoaded = useSelector((state) => selectIsLoaded(state), shallowEqual);
  const errorsFromFormValue = useSelector((state) => errorsFromForm(state), shallowEqual);
  const stateCache = useSelector((state) => selectStateCache(state), shallowEqual);
  const { plan, payments, totalDueToday, currentView, SetUp, isPaymentNeeded, isPaymentDetailsAvailable, approvalStatus, errors, errorInfo } =
    useCheckoutHooks(stateCache);
  const [setUpOptionChanged, setSetUpOptionChanged] = useState(false);
  const checkoutLabels = window?.checkoutCQ?.output?.checkoutLabels ? window?.checkoutCQ?.output?.checkoutLabels : expressCheckoutLabels;

  const prevSetUpValue = usePrevious(SetUp);
  const notificationMessage =
    isPaymentNeeded && isPaymentDetailsAvailable ? checkoutLabels.noficationForDuePayment : checkoutLabels.preSelectedNotification;

  useEffect(() => {
    if (approvalStatus && approvalStatus !== 'AP') {
      const updateView = currentView?.CreditStatus;
      goToProcessStep(updateView, history);
    }
  }, [approvalStatus]);

  useEffect(() => {
    document.title = 'Review your order';
    checkCartidAndLQResponse('expresscheckout');
    dispatch({ type: actionTypes.GET_EXPRESS_CHECKOUT_DETAILS, data: getExpressCheckoutRequest, isCassNeeded: true });
    setNavigationTitle(isMVA(), document.title);
  }, []);

  useEffect(() => {
    props.invokeTaggingAPI();
    if (notificationMessage) {
      eventDispatcher('sendData', {
        name: 'Notification',
        svars: {
          evar13: notificationMessage,
          evar22: 'message displayed',
        },
      });
    }
  }, [!isEmpty(plan)]);
  // show banner notification when switched to self setup from appointment page
  useEffect(() => {
    if (prevSetUpValue !== undefined && getSession('changeToSelf') === 'Y') {
      setSetUpOptionChanged(true);
    }
  }, [SetUp]);

  const isProfWithOutDueToday = () => {
    if (totalDueToday !== undefined) {
      return totalDueToday <= 0.0 && SetUp.indexOf('Prof') > -1;
    }
    return false;
  };

  const onCartContinue = () => {
    // CHECK IF CONTACT INFO EDIT IS OPEN, ALL FIELDS ARE VALID AND THEN ONLY DO THE ROUTNG LOGIC
    // console.log('onCartContinue', selectCheckoutValues?.isFormErrors);
    // debugger;
    // console.log('onContinue', errorsFromFormValue);
    if (Array.isArray(errorsFromFormValue) && errorsFromFormValue?.length > 0) {
      return null;
    }
    if ((Array.isArray(errorsFromFormValue) && errorsFromFormValue?.length === 0) || errorsFromFormValue === 'default') {
      let updateView;
      if (totalDueToday && totalDueToday > 0.0) {
        if (
          payments &&
          (Object.keys(payments).length === 0 || (Object.keys(payments).length > 0 && (payments.AC === undefined || payments.AC === null)))
        ) {
          updateView = currentView?.Payment;
        } else {
          updateView = currentView?.ExpressTOS;
        }
      } else if (SetUp.indexOf('Prof') > -1) {
        updateView = currentView?.Appointment;
      } else {
        updateView = currentView?.ExpressTOS;
      }
      goToProcessStep(updateView, history);
    }
  };

  if (!isLoaded) {
    return null;
  }
  const onClickPromo = (value) => {
    dispatch({
      type: actionTypes.EXPRESS_CHECKOUT_VALIDATE_PROMO_CODE,
      data: value,
    });
  };
  return (
    <FWAContainer className="" enableLiveSupport topBufferAuto>
      <LANDING>
        {errorInfo.status ? (
          <Margin top="12px">
            <div className="content">
              <Notification
                type="warning"
                title={errors?.[0]?.notifyMessage}
                fullBleed={false}
                inline={false}
                disableFocus
                data-track={errors?.[0]?.notifyMessage}
              />
            </div>
          </Margin>
        ) : (
          <>
            {!isMobile() && (
              <Margin top="12px">
                <div className="content">
                  <Notification
                    type="info"
                    title={setUpOptionChanged ? checkoutLabels?.updateSelectedNotification : notificationMessage}
                    fullBleed={false}
                    inline={false}
                    disableFocus
                    data-track={`close| ${setUpOptionChanged ? checkoutLabels?.updateSelectedNotification : notificationMessage}`}
                  />
                </div>
              </Margin>
            )}
            <div className="content">
              <div className="mainContainer">
                <Margin top={isMobile() ? '22px' : '32px'} bottom={isMobile() ? '22px' : '32px'}>
                  <Title size="large" primitive="p" bold>
                    {checkoutLabels.expressCheckoutHeader}
                  </Title>
                </Margin>
                {isMobile() && (
                  <Notification
                    type="info"
                    title={setUpOptionChanged ? checkoutLabels?.updateSelectedNotification : notificationMessage}
                    fullBleed={false}
                    inline={false}
                    disableFocus
                    data-track={`close| ${setUpOptionChanged ? checkoutLabels?.updateSelectedNotification : notificationMessage}`}
                  />
                )}
                <Plans {...props} />
                <Accessories />
                <CheckoutInfo />
              </div>
              <div className="orderContainer">
                <Margin top="40px" left={isMobile() ? '20px' : '0'} right={isMobile() ? '20px' : ''}>
                  {isDesktopResolution() && <DesktopCart pageName="expresscheckout" clickPromo={onClickPromo} {...props} />}
                </Margin>
                {/* {isMobile() && <StickyCart btnName="continue" showCart= {false}/>} */}
                {!isDesktopResolution() && (
                  <>
                    <Margin left="6px" bottom="8px">
                      <Title size="medium" bold>
                        {checkoutLabels.orderSummary}
                      </Title>
                    </Margin>
                    <MobileCart pageName="expresscheckout" clickPromo={onClickPromo} />
                  </>
                )}
                <Margin top="16px">
                  <Button
                    data-testid="continueCartCTA"
                    analyticstrack="5G-NextSetupStep"
                    ariaLabel="Continue"
                    use="primary"
                    data-track='{"type":"link","name":"continue|express checkout","data":{"page":{"throttle":"nsa version|vhi|ec y"},"target":{"sandBox":"FWA_Express_T_Checkout"}}}'
                    onClick={() => {
                      onCartContinue();
                    }}
                    size="large"
                    width="100%"
                  >
                    {isProfWithOutDueToday() ? checkoutLabels.scheduleCTA : checkoutLabels.continueCta}
                  </Button>
                </Margin>
              </div>
            </div>
          </>
        )}
      </LANDING>
    </FWAContainer>
  );
};

ExpressCheckout.propTypes = {
  invokeTaggingAPI: PropTypes.func,
};

export default ExpressCheckout;
